import React, { useState, useEffect } from 'react';
import { VikingData, PlayerData, CardData, VikingsResponse } from '../types/GameTypes';
import PlayerDashboard from './PlayerDashboard';
import './GameControls.css';

interface GameControlsProps {
  gameId: string;
  onPlayCard: (cardId: string) => void;
}

const GameControls: React.FC<GameControlsProps> = ({ gameId, onPlayCard }) => {
  const [vikings, setVikings] = useState<VikingData[]>([]);
  const [currentPlayer, setCurrentPlayer] = useState<PlayerData | null>(null);
  const [marketCards, setMarketCards] = useState<CardData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (gameId) {
      fetchGameState();
    }
  }, [gameId]);

  const fetchGameState = async () => {
    if (!gameId) return;

    try {
      setIsLoading(true);
      // Fetch all vikings in the game
      const response = await fetch(`https://vikings.tenb.uk/api/viking/game/${gameId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch Vikings: ${response.status}`);
      }
      const data: VikingsResponse = await response.json();
      setVikings(data.vikings);

      // For now, assume the first viking is the current player
      if (data.vikings.length > 0) {
        // This is a simplified PlayerData object. A real implementation would fetch hand, deck, etc.
        const player: PlayerData = {
          viking: data.vikings[0],
          hand: [],
          deckCount: 0,
          discardCount: 0
        };
        setCurrentPlayer(player);
      }

      // In a real app, you would fetch market cards from an API
      setMarketCards([
        { id: 'market-1', name: 'Shield Wall', description: 'Gain 10 armor', cost: 3 },
        { id: 'market-2', name: 'Rage', description: '+5 attack this turn', cost: 4 },
        { id: 'market-3', name: 'Sprint', description: 'Move 2 spaces', cost: 2 },
      ]);

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load game state');
    } finally {
      setIsLoading(false);
    }
  };

  const handleHeal = async () => {
    if (!gameId || !currentPlayer) return;

    try {
      const response = await fetch(`https://vikings.tenb.uk/api/viking/${gameId}/${currentPlayer.viking.id}/heal`, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to heal: ${response.status}`);
      }

      await fetchGameState(); // Refresh the game state
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to heal');
    }
  };


  if (isLoading) {
    return <div className="game-controls">Loading game...</div>;
  }

  if (error) {
    return <div className="game-controls error">{error}</div>;
  }

  return (
    <div className="game-controls">
      <PlayerDashboard
        player={currentPlayer}
        marketCards={marketCards}
        onHeal={handleHeal}
        onPlayCard={onPlayCard}
      />
    </div>
  );
};

export default GameControls;